import { supabase } from './supabase';

// Messaging Service
export const messagingService = {
  // Get all threads for current user
  async getUserThreads() {
    try {
      const { data: threads, error } = await supabase?.from('message_threads')?.select(`
          *,
          created_by_profile:user_profiles!message_threads_created_by_fkey(id, full_name, role, avatar_url),
          participants:thread_participants(
            user_id,
            last_read_at,
            user_profile:user_profiles(id, full_name, role, avatar_url)
          ),
          messages(id, content, created_at, is_read)
        `)?.order('updated_at', { ascending: false });

      if (error) throw error;
      return { data: threads, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get messages for a specific thread
  async getThreadMessages(threadId) {
    try {
      const { data: messages, error } = await supabase?.from('messages')?.select(`
          *,
          sender:user_profiles!messages_sender_id_fkey(id, full_name, role, avatar_url)
        `)?.eq('thread_id', threadId)?.order('created_at', { ascending: true });

      if (error) throw error;
      return { data: messages, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Create new message thread
  async createThread(subject, participantIds) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Create thread
      const { data: thread, error: threadError } = await supabase?.from('message_threads')?.insert({ subject, created_by: user?.id })?.select()?.single();

      if (threadError) throw threadError;

      // Add participants (including creator)
      const participants = [user?.id, ...participantIds]?.map(userId => ({
        thread_id: thread?.id,
        user_id: userId
      }));

      const { error: participantsError } = await supabase?.from('thread_participants')?.insert(participants);

      if (participantsError) throw participantsError;

      return { data: thread, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Send message in thread
  async sendMessage(threadId, content) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: message, error } = await supabase?.from('messages')?.insert({
          thread_id: threadId,
          sender_id: user?.id,
          content
        })?.select(`
          *,
          sender:user_profiles!messages_sender_id_fkey(id, full_name, role, avatar_url)
        `)?.single();

      if (error) throw error;
      return { data: message, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Mark thread as read
  async markThreadAsRead(threadId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase?.from('thread_participants')?.update({ last_read_at: new Date()?.toISOString() })?.eq('thread_id', threadId)?.eq('user_id', user?.id);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error };
    }
  },

  // Get unread message count
  async getUnreadCount() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: threads, error } = await supabase?.from('thread_participants')?.select(`
          thread_id,
          last_read_at,
          thread:message_threads(
            updated_at
          )
        `)?.eq('user_id', user?.id);

      if (error) throw error;

      const unreadCount = threads?.filter(tp => {
        const lastRead = tp?.last_read_at ? new Date(tp?.last_read_at) : null;
        const lastUpdate = tp?.thread?.updated_at ? new Date(tp?.thread?.updated_at) : null;
        return !lastRead || (lastUpdate && lastUpdate > lastRead);
      })?.length || 0;

      return { data: unreadCount, error: null };
    } catch (error) {
      return { data: 0, error };
    }
  },

  // Subscribe to new messages in a thread
  subscribeToThread(threadId, callback) {
    const subscription = supabase?.channel(`thread:${threadId}`)?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `thread_id=eq.${threadId}`
        },
        async (payload) => {
          // Fetch full message with sender details
          const { data: message } = await supabase?.from('messages')?.select(`
              *,
              sender:user_profiles!messages_sender_id_fkey(id, full_name, role, avatar_url)
            `)?.eq('id', payload?.new?.id)?.single();

          if (message) callback(message);
        }
      )?.subscribe();

    return subscription;
  },

  // Subscribe to all user threads for notifications
  subscribeToUserThreads(callback) {
    const subscription = supabase?.channel('user-threads')?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages'
        },
        async (payload) => {
          const { data: { user } } = await supabase?.auth?.getUser();
          if (!user) return;

          // Check if user is participant in this thread
          const { data: isParticipant } = await supabase?.from('thread_participants')?.select('id')?.eq('thread_id', payload?.new?.thread_id)?.eq('user_id', user?.id)?.single();

          if (isParticipant && payload?.new?.sender_id !== user?.id) {
            // Fetch full message details
            const { data: message } = await supabase?.from('messages')?.select(`
                *,
                sender:user_profiles!messages_sender_id_fkey(id, full_name, role, avatar_url),
                thread:message_threads(subject)
              `)?.eq('id', payload?.new?.id)?.single();

            if (message) callback(message);
          }
        }
      )?.subscribe();

    return subscription;
  },

  // Get all users for starting new conversation
  async getAllUsers() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: users, error } = await supabase?.from('user_profiles')?.select('id, full_name, role, avatar_url, email')?.neq('id', user?.id)?.eq('is_active', true)?.order('full_name');

      if (error) throw error;
      return { data: users, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};

// Gym Service
export const gymService = {
  // Get all active gyms
  async getActiveGyms() {
    try {
      const { data: gyms, error } = await supabase?.from('gyms')?.select('*')?.eq('is_active', true)?.order('name');

      if (error) throw error;
      return { data: gyms, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get gym by ID
  async getGymById(gymId) {
    try {
      const { data: gym, error } = await supabase?.from('gyms')?.select('*')?.eq('id', gymId)?.single();

      if (error) throw error;
      return { data: gym, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};

// Payment Reference Service
export const paymentService = {
  // Generate payment reference for pay-at-gym
  async generatePaymentReference(amount, gymId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Call database function to generate unique reference number
      const { data: refNumber, error: refError } = await supabase?.rpc('generate_payment_reference');

      if (refError) throw refError;

      // Create payment reference record
      const { data: paymentRef, error } = await supabase?.from('payment_references')?.insert({
          reference_number: refNumber,
          user_id: user?.id,
          amount,
          payment_status: 'pending',
          payment_method: 'pay_at_gym',
          gym_id: gymId
        })?.select()?.single();

      if (error) throw error;
      return { data: paymentRef, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get payment reference by reference number
  async getPaymentByReference(referenceNumber) {
    try {
      const { data: payment, error } = await supabase?.from('payment_references')?.select(`
          *,
          user:user_profiles(id, full_name, email),
          gym:gyms(id, name, address)
        `)?.eq('reference_number', referenceNumber)?.single();

      if (error) throw error;
      return { data: payment, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Update payment status (staff only)
  async updatePaymentStatus(paymentId, status, notes = null) {
    try {
      const updateData = {
        payment_status: status,
        ...(notes && { notes }),
        ...(status === 'completed' && { completed_at: new Date()?.toISOString() })
      };

      const { data: payment, error } = await supabase?.from('payment_references')?.update(updateData)?.eq('id', paymentId)?.select()?.single();

      if (error) throw error;

      // If payment completed, activate user account
      if (status === 'completed') {
        const { error: profileError } = await supabase?.from('user_profiles')?.update({ 
            account_status: 'active',
            payment_reference_id: paymentId
          })?.eq('id', payment?.user_id);

        if (profileError) throw profileError;
      }

      return { data: payment, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get user's payment references
  async getUserPayments() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: payments, error } = await supabase?.from('payment_references')?.select(`
          *,
          gym:gyms(id, name, address)
        `)?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (error) throw error;
      return { data: payments, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }
};
